<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import DropDown from '@/components/Inputs/DropDown.vue';
import TextInput from '@/components/Inputs/TextInput.vue';
import ButtonText from '@/shared/components/UI/Buttons/ButtonText.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import RadioButton from '@/shared/components/UI/RadioButton.vue';
import { CONTACT_UUID_STORAGE_KEY } from '@/shared/composables/useContactUuid';
import { useTranslations } from '@/shared/composables/useTranslations';
import {
  type CraftName,
  type CraftSelection,
  type SkillLevel,
  useUserStore
} from '@/shared/pinia/modules/user';
import type { UserDetailsFormField } from '@/shared/tracking/user-details-form';
import { isEmailValid } from '@/shared/utils/email';
import { generateContactUuid } from '@/shared/utils/emarsys';
import { hobbiiLocalStorage } from '@/shared/utils/web-storage';

const { t } = useTranslations();
const userStore = useUserStore();

const props = defineProps<{
  active: boolean;
  submitLabel: string;
  loading?: boolean;
  error?: string | null;
}>();

const emit = defineEmits<{
  'update:active': [value: boolean];
  'field-filled': [field: UserDetailsFormField];
  submitted: [
    details: {
      firstName: string;
      email: string;
      crafts: CraftSelection[];
      website: string;
      formFilledInMs: number;
    }
  ];
}>();

type CraftChoice = CraftName | 'Both';

const SKILL_LEVELS: SkillLevel[] = ['Beginner', 'Intermediate', 'Experienced'];

const firstName = ref('');
const customerEmail = ref('');
const emailIsValid = ref<boolean | null>(null);
const showEmailError = ref(false);
const craftChoice = ref<CraftChoice | null>(null);
const skillByCraft = ref<Record<CraftName, SkillLevel | null>>({
  Knit: null,
  Crochet: null
});

// Bot-protection: a hidden honeypot input real users never fill, and the
// time the form has been open, both read by the backend's spam check.
const website = ref('');
const formOpenedAt = ref(Date.now());
const filledFields = ref(new Set<UserDetailsFormField>());
watch(
  () => props.active,
  (isActive) => {
    if (isActive) {
      formOpenedAt.value = Date.now();
      filledFields.value = new Set();
    }
  }
);

// Fires once per field per modal-open — first time it's genuinely filled,
// not on every keystroke, and not again if the user edits it further.
const trackFieldFilled = (field: UserDetailsFormField) => {
  if (filledFields.value.has(field)) return;
  filledFields.value.add(field);
  emit('field-filled', field);
};

const selectedCrafts = computed<CraftName[]>(() => {
  if (craftChoice.value === 'Both') return ['Crochet', 'Knit'];
  if (craftChoice.value) return [craftChoice.value];
  return [];
});

const crafts = computed<CraftSelection[]>(() =>
  selectedCrafts.value.reduce<CraftSelection[]>((acc, craft) => {
    const skillLevel = skillByCraft.value[craft];
    if (skillLevel) acc.push({ name: craft, skillLevel });
    return acc;
  }, [])
);

watch(craftChoice, (value) => {
  if (value) trackFieldFilled('craft');
});

// "Skill" is one logical field even when "Both" renders two dropdowns —
// counts as filled once every currently-selected craft has a skill level.
watch(
  () =>
    selectedCrafts.value.length > 0 &&
    crafts.value.length === selectedCrafts.value.length,
  (isComplete) => {
    if (isComplete) trackFieldFilled('skill');
  }
);

const formIsValid = computed(() => {
  if (!emailIsValid.value || !firstName.value.trim()) return false;
  if (selectedCrafts.value.length === 0) return false;
  return selectedCrafts.value.every((craft) => skillByCraft.value[craft]);
});

const craftLabel = (craft: CraftName) =>
  craft === 'Knit'
    ? t('account.crafters.shared.craft_type.knit')
    : t('account.crafters.shared.craft_type.crochet');

const skillLevelLabel = (skillLevel: SkillLevel) =>
  t(`global.user_details_form.skill_levels.${skillLevel.toLowerCase()}`);

const skillOptions = computed(() =>
  SKILL_LEVELS.map((skillLevel) => ({
    value: skillLevel,
    text: skillLevelLabel(skillLevel)
  }))
);

const onFirstNameBlur = () => {
  if (firstName.value.trim()) trackFieldFilled('first_name');
};

const validateEmailInput = (e: Event) => {
  const email = customerEmail.value.trim();

  if (!email) {
    emailIsValid.value = null;
    showEmailError.value = false;
    customerEmail.value = email;
  } else if (isEmailValid(email)) {
    emailIsValid.value = true;
    showEmailError.value = false;
    customerEmail.value = email;
    trackFieldFilled('email');
  } else {
    emailIsValid.value = false;
    if (e.type === 'blur') {
      showEmailError.value = true;
      customerEmail.value = email;
    } else {
      showEmailError.value = false;
    }
  }
};

const onFormSubmit = () => {
  const trimmedEmail = customerEmail.value.trim();
  customerEmail.value = trimmedEmail;

  // A guest (no Shopify session) has no other identity to attribute a later
  // pattern download to — useContactUuid() reads this back for exactly that,
  // once the native form's redirect has torn down everything else in memory.
  hobbiiLocalStorage.setItem(
    CONTACT_UUID_STORAGE_KEY,
    generateContactUuid(trimmedEmail)
  );

  userStore.setCapturedProfile({
    firstName: firstName.value,
    lastName: '',
    email: trimmedEmail,
    crafts: crafts.value
  });

  // Emit before the native form submits and navigates away, so the parent can
  // persist whatever it needs (e.g. for a follow-up call after the redirect)
  // while this component's state still exists to read.
  emit('submitted', {
    firstName: firstName.value,
    email: trimmedEmail,
    crafts: crafts.value,
    website: website.value,
    formFilledInMs: Date.now() - formOpenedAt.value
  });

  const userDetailsForm =
    document.querySelector<HTMLFormElement>('#user-details-form');
  if (!userDetailsForm) return;

  const firstNameInput = userDetailsForm.querySelector<HTMLInputElement>(
    'input[name="contact[first_name]"]'
  );
  const emailInput = userDetailsForm.querySelector<HTMLInputElement>(
    'input[name="contact[email]"]'
  );

  if (firstNameInput) firstNameInput.value = firstName.value;
  if (emailInput) emailInput.value = trimmedEmail;

  userDetailsForm.submit();
};
</script>

<template>
  <Modal
    :active="active"
    size="md"
    :footer-divider="false"
    padded
    @update:active="emit('update:active', $event)"
  >
    <template #header>
      <slot name="header"></slot>
    </template>
    <template #body>
      <form
        class="flex w-full flex-col gap-6 text-black"
        @submit.prevent="onFormSubmit"
      >
        <input
          v-model="website"
          type="text"
          name="website"
          class="absolute left-[-10000px]"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
        />

        <TextInput
          v-model="firstName"
          type="text"
          autocomplete="given-name"
          required
          class="!px-3"
          @blur="onFirstNameBlur"
        >
          <template #label>{{ t('global.user_details_form.name') }}</template>
        </TextInput>

        <TextInput
          v-model="customerEmail"
          type="email"
          autocomplete="email"
          required
          class="!px-3"
          :error="showEmailError"
          @blur="validateEmailInput"
          @input="validateEmailInput"
        >
          <template #label>{{ t('global.user_details_form.email') }}</template>
          <template v-if="showEmailError" #error>
            {{ t('global.errors.form_validation.invalid_email') }}
          </template>
        </TextInput>

        <div class="flex flex-col gap-4">
          <p class="font-semibold">
            {{ t('global.user_details_form.craft_label') }}
          </p>
          <RadioButton v-model="craftChoice" value="Crochet">
            <span :class="{ 'font-bold': craftChoice === 'Crochet' }">
              {{ t('account.crafters.shared.craft_type.crochet') }}
            </span>
          </RadioButton>
          <RadioButton v-model="craftChoice" value="Knit">
            <span :class="{ 'font-bold': craftChoice === 'Knit' }">
              {{ t('account.crafters.shared.craft_type.knit') }}
            </span>
          </RadioButton>
          <RadioButton v-model="craftChoice" value="Both">
            <span :class="{ 'font-bold': craftChoice === 'Both' }">
              {{ t('global.user_details_form.craft_both') }}
            </span>
          </RadioButton>
        </div>

        <div v-if="selectedCrafts.length" class="flex flex-col gap-2">
          <p
            class="font-semibold"
            :class="{ 'mb-4': selectedCrafts.length > 1 }"
          >
            {{ t('global.user_details_form.skill_level_label') }}
          </p>
          <DropDown
            v-for="(craft, index) in selectedCrafts"
            :key="craft"
            v-model="skillByCraft[craft]"
            :options="skillOptions"
            :placeholder="t('global.user_details_form.skill_level_placeholder')"
            :class="{ 'mt-4': index > 0 }"
          >
            <template v-if="selectedCrafts.length > 1" #label>
              {{ craftLabel(craft) }}
            </template>
          </DropDown>
        </div>

        <div v-if="error" class="text-error">{{ error }}</div>

        <ButtonText
          type="submit"
          :disabled="!formIsValid || loading"
          :loading="loading"
          theme="primary"
          size="lg"
          full-width
          class="mt-4"
        >
          {{ submitLabel }}
          <template v-if="$slots['submit-icon']" #icon>
            <slot name="submit-icon"></slot>
          </template>
        </ButtonText>
      </form>
    </template>
    <template #footer>
      <slot name="footer"></slot>
    </template>
  </Modal>
</template>
